import { Flex } from '@brightdrop/bd-ui';
import { cx } from '@emotion/css';
import type { NavBarItem } from '@gm-commercial/navbar-model';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import ArrowDownIcon from '../../assets/icons/new/arrowDownIcon.svg?react';
import ArrowUpIcon from '../../assets/icons/new/arrowUpIcon.svg?react';
import NavDrawerItem from './NavDrawerItem';
import useStyles from './NavDrawerItem.styles';

export type ContentNavDrawerItemProps = NavBarItem & {
  isNavDrawerOpen?: boolean;
  currentPagePath?: string;
  typographyClass?: 'body_medium' | 'body_regular';
};

/**
 * Normalizes a given path using the current origin.
 * @param {string} path - The path to normalize.
 * @returns {string} - The normalized pathname.
 */
const normalizePath = (path: string): string => {
  return new URL(path, window.location.origin).pathname || path;
};

const ContentNavDrawerItem = (item: ContentNavDrawerItemProps): JSX.Element => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const [isItemOpen, setIsItemOpen] = useState(true);
  const [isCollapsedButtonHovered, setIsCollapsedButtonHovered] =
    useState<boolean>(false);
  const [isCollapsedButtonSticky, setIsCollapsedButtonSticky] =
    useState<boolean>(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const navDrawerItemLabel = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLLIElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);

  /**
   * Normalized path of the current page, if defined.
   * This variable is used throughout the component to determine if the current page matches an item's path
   * to drive various styles and behavior.
   * @type {string | undefined}
   */
  const normalizedCurrentPagePath: string | undefined = item.currentPagePath
    ? normalizePath(item.currentPagePath)
    : undefined;

  /**
   * Determines if any child path matches the normalized current page path.
   * Children with `matchNestedPaths` also match pages nested under their path.
   * If so, this ContentNavDrawerItem is considered an active parent container,
   * which affects styling. Applies when nav drawer is open or when a child is selected.
   * @type {boolean}
   */
  const isActiveParent: boolean =
    item?.children?.some(
      ({ path: childPath, matchNestedPaths }: NavBarItem) => {
        if (!childPath || !normalizedCurrentPagePath) return false;
        const normalizedChildPath = normalizePath(childPath);
        return (
          normalizedChildPath === normalizedCurrentPagePath ||
          (!!matchNestedPaths &&
            normalizedCurrentPagePath.startsWith(`${normalizedChildPath}/`))
        );
      }
    ) ?? false;

  /**
   * Checks if the parent path matches the normalized current page path.
   * This is used to determine if we need to open the dropdown when the parent list item is clicked.
   * Otherwise, only the arrow should toggle the open/close state.
   * @type {boolean}
   */
  const doesParentPathMatchCurrentPagePath: boolean = item.path
    ? normalizePath(item.path) === normalizedCurrentPagePath
    : false;

  const isEverythingOpen: boolean = Boolean(item.isNavDrawerOpen && isItemOpen);

  /**
   * Determines if the drawer is closed and the item is interacted with (hovered or sticky).
   * Used to drive collapsed state interaction behaviors.
   * @type {boolean}
   */
  const isDrawerClosedAndItemInteracted: boolean =
    !item.isNavDrawerOpen &&
    (isCollapsedButtonHovered || isCollapsedButtonSticky);

  /**
   * Determines if the children should be shown based on whether everything is open
   * or if the drawer is closed and the item is interacted with.
   * @type {boolean}
   */
  const shouldShowChildren: boolean = Boolean(
    isEverythingOpen || isDrawerClosedAndItemInteracted
  );

  const onNavigate = useCallback((callback?: () => void) => {
    if (callback) {
      callback();
    }
  }, []);

  const handleParentListItemClick = (e: React.MouseEvent<HTMLLIElement>) => {
    // If parent has children and in collapsed state, show the flyout and make it sticky
    if (item.children && item.children.length > 0 && !item.isNavDrawerOpen) {
      // In collapsed state, make the flyout sticky
      setIsCollapsedButtonSticky(true);
    } else if (
      !doesParentPathMatchCurrentPagePath &&
      item.path &&
      // When drawer is expanded, allow clicking anywhere except the toggle button
      (item.isNavDrawerOpen || e.target === navDrawerItemLabel.current) &&
      // Don't navigate if clicking on the toggle button
      !(e.target as Element).closest(
        `[data-testid="${item.testId}-toggle-btn"]`
      )
    ) {
      // Navigate to parent path when clicking on the parent container (when expanded) or specific elements
      if (!isItemOpen) {
        setIsItemOpen(true);
      }
      navigate(item.path);
      onNavigate(item.callback);
    }
  };

  const handleParentListItemKeyDown = (
    e: React.KeyboardEvent<HTMLLIElement>
  ) => {
    const { key } = e;

    if (key === 'Enter' || key === ' ') {
      e.preventDefault();

      const focusedElement = document.activeElement;

      // Navigate to focused child if in flyout, otherwise show flyout for parent
      if (
        flyoutRef?.current?.contains(focusedElement) &&
        focusedElement?.closest('[data-path]')?.getAttribute('data-path')
      ) {
        const childPath = focusedElement
          ?.closest('[data-path]')
          ?.getAttribute('data-path');
        if (childPath) {
          navigate(childPath);
        }

        onNavigate(item.callback);
      } else if (
        item.children &&
        item.children.length > 0 &&
        !item.isNavDrawerOpen
      ) {
        // If parent has children, show the flyout
        setIsCollapsedButtonSticky(true);
      } else if (item.path) {
        // If no children, navigate to parent path
        navigate(item.path);
        onNavigate(item.callback);
      }

      if (!isCollapsedButtonSticky && !item.isNavDrawerOpen) {
        setIsCollapsedButtonSticky(true);
      }
    } else if (
      key === 'Escape' &&
      !item.isNavDrawerOpen &&
      isCollapsedButtonSticky
    ) {
      setIsCollapsedButtonSticky(false);
    }
  };
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        flyoutRef.current &&
        !flyoutRef.current.contains(event.target as Node) &&
        !item.isNavDrawerOpen &&
        (isCollapsedButtonHovered || isCollapsedButtonSticky)
      ) {
        setIsCollapsedButtonHovered(false);
        setIsCollapsedButtonSticky(false);
      }
    },
    [isCollapsedButtonHovered, isCollapsedButtonSticky, item.isNavDrawerOpen]
  );

  const handleGlobalKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Close flyout when Enter is pressed on a different navigation item
      if (
        (event.key === 'Enter' || event.key === ' ') &&
        !item.isNavDrawerOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        flyoutRef.current &&
        !flyoutRef.current.contains(event.target as Node)
      ) {
        const target = event.target as Element;
        const isTargetNavItem = target.closest('[id*="nav-drawer-item"]');
        if (
          isTargetNavItem &&
          containerRef.current &&
          !containerRef.current.contains(isTargetNavItem)
        ) {
          // Close the flyout when Enter is pressed on a different navigation item
          setIsCollapsedButtonHovered(false);
          setIsCollapsedButtonSticky(false);
        }
      }

      // Close flyout when Tab is pressed and focus moves to a different nav item
      if (event.key === 'Tab' && !item.isNavDrawerOpen) {
        const target = event.target as Element;
        const isTargetNavItem = target.closest('[id*="nav-drawer-item"]');

        if (
          isTargetNavItem &&
          containerRef.current &&
          !containerRef.current.contains(isTargetNavItem) &&
          (isCollapsedButtonHovered || isCollapsedButtonSticky)
        ) {
          setIsCollapsedButtonHovered(false);
          setIsCollapsedButtonSticky(false);
        }
      }
    },
    [isCollapsedButtonHovered, isCollapsedButtonSticky, item.isNavDrawerOpen]
  );
  const toggleContent = useCallback(() => {
    setIsItemOpen((previousValue) => !previousValue);
  }, []);

  const handleChildItemInteraction = useCallback(() => {
    // Close the flyout when a child item is clicked or Enter is pressed
    if (!item.isNavDrawerOpen) {
      setIsCollapsedButtonHovered(false);
      setIsCollapsedButtonSticky(false);
    }
  }, [item.isNavDrawerOpen]);

  const handleMouseEnter = useCallback(() => {
    if (!item.isNavDrawerOpen) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      setIsCollapsedButtonHovered(true);
    }
  }, [item.isNavDrawerOpen]);

  const handleMouseLeave = useCallback(() => {
    if (!item.isNavDrawerOpen) {
      // Only close on mouse leave if we're not hovering over the flyout
      hoverTimeoutRef.current = setTimeout(() => {
        // Check if we're still hovering over the flyout before closing
        if (flyoutRef.current && !flyoutRef.current.matches(':hover')) {
          setIsCollapsedButtonHovered(false);
        }
      }, 100); // Small delay to prevent flickering
    }
  }, [item.isNavDrawerOpen]);

  const handleFlyoutMouseEnter = useCallback(() => {
    if (!item.isNavDrawerOpen) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      setIsCollapsedButtonHovered(true);
    }
  }, [item.isNavDrawerOpen]);

  const handleFlyoutMouseLeave = useCallback(() => {
    if (!item.isNavDrawerOpen) {
      // Only close if we're not hovering over the parent
      hoverTimeoutRef.current = setTimeout(() => {
        if (containerRef.current && !containerRef.current.matches(':hover')) {
          setIsCollapsedButtonHovered(false);
        }
      }, 100);
    }
  }, [item.isNavDrawerOpen]);
  // Handle clicking outside the container to close the collapsed interaction state
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleGlobalKeyDown);

    // Add focus event listener to detect when focus moves to a different nav item
    const handleGlobalFocus = (event: FocusEvent) => {
      if (
        !item.isNavDrawerOpen &&
        (isCollapsedButtonHovered || isCollapsedButtonSticky)
      ) {
        const target = event.target as Element;
        const isTargetNavItem = target.closest('[id*="nav-drawer-item"]');
        if (
          isTargetNavItem &&
          containerRef.current &&
          !containerRef.current.contains(isTargetNavItem)
        ) {
          // Close flyout when focus moves to a different nav item
          setIsCollapsedButtonHovered(false);
          setIsCollapsedButtonSticky(false);
        }
      }
    };

    document.addEventListener('focusin', handleGlobalFocus);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleGlobalKeyDown);
      document.removeEventListener('focusin', handleGlobalFocus);
    };
  }, [
    handleClickOutside,
    handleGlobalKeyDown,
    item.isNavDrawerOpen,
    isCollapsedButtonHovered,
    isCollapsedButtonSticky,
  ]);

  // Clear hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // If the item has no children, render as a link item
  if (!item.children || item.children.length === 0) {
    return <LinkNavDrawerItem {...item} />;
  }

  return (
    <ListItem
      data-testid={item.testId}
      id={`${item.testId}-nav-drawer-item`}
      className={cx(
        classes.listItem,
        'contentItem',
        classes.nestingContainer,
        item.isNavDrawerOpen && 'isNavDrawerOpen'
      )}
      role="button"
      aria-expanded={shouldShowChildren}
      aria-controls={shouldShowChildren ? `${item.testId}-submenu` : undefined}
      aria-haspopup="true"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={() => {
        if (!item.isNavDrawerOpen) {
          setIsCollapsedButtonSticky(true);
        }
      }}
      onClick={handleParentListItemClick}
      onKeyDown={handleParentListItemKeyDown}
      tabIndex={!item.isNavDrawerOpen ? 0 : -1}
      ref={containerRef}
    >
      <Flex
        style={!isActiveParent ? { cursor: 'pointer' } : undefined} // TODO: Move to styles.ts file when UNIFIED_ACCOUNT_NAV condition is removed
        className={cx(
          'nestingContainerTitle',
          isActiveParent && classes.activeItem,
          !doesParentPathMatchCurrentPagePath && 'parentClickable'
        )}
      >
        <Flex className={'nestingContainerTitleIconText'}>
          {item.icon && (
            <ListItemIcon
              className={classes.itemIcon}
              data-testid={`${item.testId}-icon`}
            >
              {item.icon}
            </ListItemIcon>
          )}
          {item.isNavDrawerOpen && (
            <>
              <Typography
                variant={'body_medium'}
                className={cx(
                  classes.itemText,
                  'contentItem',
                  'nestingContainerTitleLabel'
                )}
                ref={navDrawerItemLabel}
              >
                {item.label}
              </Typography>
              <Button
                className={classes.toggleIcon}
                tabIndex={0}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  toggleContent();
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleContent();
                  }
                }}
                data-testid={`${item.testId}-toggle-btn`}
              >
                {item.isNavDrawerOpen &&
                  (isItemOpen ? (
                    <ArrowUpIcon className="noFill" />
                  ) : (
                    <ArrowDownIcon className="noFill" />
                  ))}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
      {shouldShowChildren && (
        <Box
          id={`${item.testId}-submenu`}
          className={cx(
            classes.contentNavDrawerItemContainer,
            isDrawerClosedAndItemInteracted ? 'collapsedHover' : undefined
          )}
          ref={flyoutRef}
          onMouseEnter={handleFlyoutMouseEnter}
          onMouseLeave={handleFlyoutMouseLeave}
        >
          <Box
            className={cx(
              classes.contentNavDrawerItemInnerContainer,
              isDrawerClosedAndItemInteracted ? 'collapsedHover' : undefined
            )}
          >
            <List>
              {item.children?.map((child) => (
                <NavDrawerItem
                  typographyClass="body_regular"
                  key={`child-${child.testId}`}
                  {...child}
                  isNavDrawerOpen={
                    item.isNavDrawerOpen ||
                    isDrawerClosedAndItemInteracted ||
                    false
                  }
                  callback={() => {
                    // Call the child's callback if it exists
                    if (child.callback) {
                      child.callback();
                    }
                    // Also call the parent's callback if it exists
                    if (item.callback) {
                      item.callback();
                    }
                    // Close the flyout
                    handleChildItemInteraction();
                  }}
                />
              ))}
            </List>
          </Box>
        </Box>
      )}
    </ListItem>
  );
};

const LinkNavDrawerItem = (item: ContentNavDrawerItemProps): JSX.Element => {
  const { classes } = useStyles();
  const onNavigate = useCallback((callback?: () => void) => {
    if (callback) {
      callback();
    }
  }, []);

  const handleItemClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Prevent the event from propagating to parent drawer element and triggering click actions.
    e.stopPropagation();
    onNavigate(item.callback);
  };

  return (
    <NavLink
      id={`${item.testId}-nav-drawer-item`}
      to={item.path || ''}
      end={!item.matchNestedPaths}
      className={({ isActive }) =>
        cx('Mui-selected', classes.listItem, isActive && classes.activeItem)
      }
      data-testid={item.testId}
      data-path={item.path}
      onClick={handleItemClick}
    >
      {item.icon && (
        <ListItemIcon
          className={classes.itemIcon}
          data-testid={`${item.testId}-icon`}
        >
          {item.icon}
        </ListItemIcon>
      )}
      {item.isNavDrawerOpen && (
        <Typography variant={item.typographyClass} className={classes.itemText}>
          {item.label}
        </Typography>
      )}
    </NavLink>
  );
};

export default ContentNavDrawerItem;
